import { useMutation, useQuery } from '@apollo/client';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { formatDate } from '@procureai/util';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { AstroAbsenceInput, LoadAbsencesQueryVariables } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';

import { DELETE_ABSENCE_MUTATION } from './DeleteAbsence.gql';
import { LOAD_ABSENCES_QUERY } from './LoadAbsences.gql';
import { UPSERT_ABSENCE_MUTATION } from './UpsertAbsence.gql';

export enum SmartLinkSaveStatus {
  DISABLED,
  UNSAVED,
  SAVED,
}

export const useUserProfileAbsences = ({ userId }: { userId: string | undefined }) => {
  const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useCustomSnackbar();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'UserProfileAbsenceWidget' });
  const { hasPermission } = usePermissions();
  const { user } = useAuth();

  const variables: LoadAbsencesQueryVariables = { userId, where: { from: formatDate(new Date()) } };

  const { data } = useQuery(LOAD_ABSENCES_QUERY, { variables, fetchPolicy: 'cache-first' });

  const isHimself = !userId || data?.astronaut.user?.primarySourceReference === user?.profile.sub;
  const canEditHis = isHimself && hasPermission(Permission.UserAvailabilityEdit);
  const canEditOther = hasPermission(Permission.UserAvailabilityManage);
  const editable = canEditHis || canEditOther;

  const [upsert] = useMutation(UPSERT_ABSENCE_MUTATION, {
    onCompleted: () => {
      enqueueSuccessSnackbar(t('success'));
    },
    onError: () => {
      enqueueErrorSnackbar(t('error.upsert'));
    },
    refetchQueries: [{ query: LOAD_ABSENCES_QUERY, variables }],
  });
  const [remove] = useMutation(DELETE_ABSENCE_MUTATION, {
    onCompleted: () => {
      enqueueSuccessSnackbar(t('success'));
    },
    onError: () => {
      enqueueErrorSnackbar(t('error.delete'));
    },
    refetchQueries: [{ query: LOAD_ABSENCES_QUERY, variables }],
  });

  const deleteAbsenceEntry = async (id: string) =>
    remove({
      variables: { userId, id },
    });

  const upsertAbsenceEntry = async (data: AstroAbsenceInput) =>
    upsert({
      variables: { userId, data },
    });

  return {
    userProfileAbsences: data?.astronaut.user.absences.nodes || [],
    deleteAbsenceEntry,
    upsertAbsenceEntry,
    editable,
  };
};
